import { useState, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import { AWS_URL } from "../../services/http.service";
import {
  FaHeart,
  FaRegHeart,
  FaStar,
  FaRegStar,
  FaStarHalfAlt,
  FaTag,
  FaShoppingCart
} from "react-icons/fa";
import { generateVariantName } from '../../components/variant/varianthelper';
import { checkWishlistItem, toggleWishlistItem } from "../../store/actions/action";

function ProductCard({ 
  product, 
  theme,   
  customerProfile,
  bussinessUuid: propBussinessUuid,
  displayBusinessName: propDisplayBusinessName,
  onAddToCart,
  previewMode = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { businessName: urlBusinessName, bussinessUuid: urlBussinessUuid } = useParams();
  
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Get wishlist state from Redux
  const wishlistStatus = useSelector(state => state.wishlist?.wishlistStatus || {});
  const checkingStatus = useSelector(state => state.wishlist?.checkingStatus || {});
  const isWishlistProcessing = useSelector(state => state.wishlist?.isProcessing || false);
  
  const isB2BMode = localStorage.getItem('b2b_Price') === 'true' || localStorage.getItem('b2b_intent') === 'true';
  const currentBussinessUuid = propBussinessUuid || urlBussinessUuid;
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === currentBussinessUuid) || businessData[0]
    : businessData;
  const displayBusinessName = urlBusinessName || business?.businessName || "Store";

  // Mock rating for demo
  const rating = product.rating || 4.5;
  const reviewCount = product.reviewCount || 128;

  // Create wishlist key for this product/variant
  const getWishlistKey = (variant = null) => {
    const variantId = variant?.variantId || selectedVariant?.variantId || null;
    return `${product.productId}_${variantId || 'null'}`;
  };

  // Check if item is wishlisted
  const isWishlisted = () => {
    const key = getWishlistKey();
    return wishlistStatus[key] || false;
  };

  // Check if wishlist is being checked for this item
  const isCheckingWishlist = () => {
    const key = getWishlistKey();
    return checkingStatus[key] || false;
  };

  // Calculate lowest variant price
  const getLowestVariantPrice = () => {
    if (!product.hasVariants || !product.variants || product.variants.length === 0) {
      return product.salePrice || "0";
    }

    let lowestPrice = Infinity;
    let lowestPriceVariant = null;

    product.variants.forEach((variant) => {
      const variantPrice = parseFloat(variant.salePrice || "0");
      if (variantPrice > 0 && variantPrice < lowestPrice) {
        lowestPrice = variantPrice;
        lowestPriceVariant = variant;
      }
    });

    if (lowestPrice === Infinity) {
      return product.salePrice || "0";
    }

    return {
      price: lowestPrice.toString(),
      variant: lowestPriceVariant,
      hasVariants: true,
    };
  };

  // Get display price based on variant selection or lowest price
  const getDisplayPrice = () => {
    if (selectedVariant?.salePrice) {
      return selectedVariant.salePrice;
    }

    const lowestPriceInfo = getLowestVariantPrice();
    if (typeof lowestPriceInfo === "object" && lowestPriceInfo.price) {
      return lowestPriceInfo.price;
    }

    return product.salePrice || "0";
  };

  // Get original price
  const getOriginalPrice = () => {
    if (selectedVariant?.mrp) {
      return selectedVariant.mrp;
    }
    return product.originalPrice || product.mrp || 0;
  };

  // Get price display text for card
  const getPriceDisplayText = () => {
    const lowestPriceInfo = getLowestVariantPrice();

    if (product.hasVariants && product.variants && product.variants.length > 0) {
      if (typeof lowestPriceInfo === "object" && lowestPriceInfo.hasVariants) {
        return `₹${lowestPriceInfo.price}`;
      }
    }

    return `₹${getDisplayPrice()}`;
  };

  // Calculate discount percentage
  const getDiscountPercentage = () => {
    const currentPrice = parseFloat(getDisplayPrice());
    const original = parseFloat(getOriginalPrice());
    
    if (original > currentPrice && currentPrice > 0) {
      return Math.round(((original - currentPrice) / original) * 100);
    }
    return 0;
  };

  // Initialize selected variant
  useEffect(() => {
    if (product.hasVariants && product.variants && product.variants.length > 0) {
      const availableVariant = product.variants.find((v) => v.quantity > 0) || product.variants[0];
      setSelectedVariant(availableVariant);
    } else {
      setSelectedVariant({
        ...product,
        variantName: product.productName,
        quantity: product.quantity || 0,
        salePrice: product.salePrice || "0",
        isBaseProduct: true,
      });
    }
  }, [product]);

  // Check wishlist status when variant changes
  useEffect(() => {
    if (product?.productId && currentBussinessUuid && selectedVariant) {
      const key = getWishlistKey(selectedVariant);
      // Only check if not already in status map
      if (wishlistStatus[key] === undefined) {
        dispatch(checkWishlistItem(
          currentBussinessUuid,
          product.productId,
          selectedVariant?.variantId || null
        ));
      }
    }
  }, [selectedVariant, product?.productId, currentBussinessUuid]);

  // Toggle wishlist
  const handleToggleWishlist = async (e) => {
    e.stopPropagation();
    
    if (!currentBussinessUuid || !product?.productId) return;
    
    await dispatch(toggleWishlistItem(currentBussinessUuid, product, selectedVariant));
  };

  // Get display values based on selected variant
  const getDisplayImage = () => {
    if (selectedVariant?.itemPicture) {
      return selectedVariant.itemPicture;
    }
    return product.itemPicture;
  };

  const getDisplayName = () => {
    if (selectedVariant?.variantName && selectedVariant.variantName !== product.productName) {
      return `${product.productName} - ${selectedVariant.variantName}`;
    }
    return product.productName;
  };

  const navigateToProductPage = (e) => {
    if (previewMode) return;
    e.preventDefault();
    navigate(`/${displayBusinessName}/store/${currentBussinessUuid}/product/${product.productId}`);
  };

  // Handle add to cart
  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product, selectedVariant);
    }
  };

  // Calculate card border radius based on theme
  const getCardBorderRadius = () => {
    switch (theme?.buttonStyle) {
      case 'pill': return '20px';
      case 'square': return '8px';
      case 'rounded':
      default: return '16px';
    }
  };

  if (!product) return null;

  const {
    productName = "Unnamed Product",
    hasVariants = false,
    variants = [],
  } = product;

  // Theme styles with fallbacks
  const themeStyles = {
    primaryColor: theme?.primaryColor || '#4f46e5',
    fontFamily: theme?.fontFamily || 'Inter, sans-serif',
  };

  const discountPercentage = getDiscountPercentage();
  const originalPrice = getOriginalPrice();
  const currentPrice = getDisplayPrice();
  const inStock = (selectedVariant?.quantity || product.quantity || 0) > 0;
  const isChecking = isCheckingWishlist();
  const isWishlistedValue = isWishlisted();

  const cardClassName = `group relative bg-white rounded-2xl overflow-hidden transition-all duration-300 ${previewMode ? "cursor-default" : "cursor-pointer hover:shadow-2xl hover:-translate-y-1"}`;

  return (
    <div
      className={cardClassName}
      style={{
        borderRadius: getCardBorderRadius(),
        fontFamily: themeStyles.fontFamily,
        boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)'
      }}
      onClick={previewMode ? undefined : navigateToProductPage}
    >
      {/* Image Container */}
      <div className="relative w-full pt-[100%] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
        <div className="absolute inset-0">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 border-3 border-gray-300 border-t-3 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          )}
          
          {getDisplayImage() && !imageError ? (
            <img
              className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              src={`${AWS_URL}${getDisplayImage()}`}
              alt={getDisplayName()}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center opacity-50">
                <span className="text-gray-500 text-xs">No image</span>
              </div>
            </div>
          )}
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Discount Badge */}
        {discountPercentage > 0 && (
          <div className="absolute top-3 left-3 z-10 animate-pulse">
            <div className="relative">
              <div className="absolute inset-0 bg-red-500 rounded-lg blur-md opacity-60"></div>
              <div className="relative bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-bold px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1">
                <FaTag className="w-3 h-3" />
                {discountPercentage}% OFF
              </div>
            </div>
          </div>
        )}

        {/* Wishlist Button */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={previewMode ? undefined : handleToggleWishlist}
            disabled={previewMode || isChecking || isWishlistProcessing}
            className={`w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${previewMode ? "cursor-not-allowed opacity-60" : "hover:shadow-xl hover:scale-110"} disabled:opacity-50`}
            title={previewMode ? t("WISHLIST.PREVIEW_DISABLED", "Wishlist disabled in preview") : t("WISHLIST.ADD_TO_WISHLIST", "Wishlist")}
          >
            {isChecking ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
                style={{ color: themeStyles.primaryColor }}
              ></div>
            ) : (
              isWishlistedValue ? (
                <FaHeart className="h-5 w-5 text-red-500" />
              ) : (
                <FaRegHeart className="h-5 w-5 text-gray-600 transition-colors" />
              )
            )}
          </button>
        </div>

        {/* Out of Stock Badge */}
        {!inStock && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10">
            <span className="bg-black/70 text-white text-xs font-medium px-4 py-2 rounded-full backdrop-blur-sm">
              Out of Stock
            </span>
          </div>
        )}

        {/* Quick View Tag */}
        {inStock && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="bg-black/70 text-white text-xs font-medium px-4 py-2 rounded-full backdrop-blur-sm">
              Quick View
            </span>
          </div>
        )}
      </div>

      {/* Product Details */}
      <div className="p-4 bg-white">
        {/* Product Name */}
        <h3 className="font-semibold text-base line-clamp-2 mb-2 text-gray-800 hover:text-blue-600 transition-colors">
          {getDisplayName()}
        </h3>

      

        {/* Price Section */}
        <div className="flex items-end gap-2 mb-2">
          <span className="text-2xl font-extrabold" style={{ color: themeStyles.primaryColor }}>
            ₹{currentPrice}
          </span>
          {originalPrice > currentPrice && (
            <>
              <span className="text-sm text-gray-400 line-through mb-1">₹{originalPrice}</span>
              <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full mb-1">
                Save ₹{originalPrice - currentPrice}
              </span>
            </>
          )}
        </div>

        {/* Discount Percentage Badge */}
        {discountPercentage > 0 && (
          <div className="mb-2">
            <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
              {discountPercentage}% Discount
            </span>
          </div>
        )}

        {/* Variant Details */}
        {hasVariants && variants.length > 0 && (() => {
          const lowestPriceInfo = getLowestVariantPrice();
          if (typeof lowestPriceInfo === "object" && lowestPriceInfo.variant) {
            const variantName = lowestPriceInfo.variant.variantName ||
              generateVariantName(
                lowestPriceInfo.variant.attributes,
                product.variantType,
              );
            return (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs font-medium text-gray-500">Available in:</span>
                <span className="text-sm font-medium text-gray-800 bg-gray-100 px-3 py-1 rounded-full">
                  {variantName}
                </span>
                {variants.length > 1 && (
                  <span className="text-xs text-blue-600 font-medium">
                    +{variants.length - 1} more
                  </span>
                )}
              </div>
            );
          }
          return null;
        })()}

    
      </div>
    </div>
  );
}
export function ProductList({ products, onAddToCart, theme, bussinessUuid, customerProfile, displayBusinessName, previewMode = false }) {
  const safeProducts = Array.isArray(products) ? products : [];

  const containerStyle = {
    fontFamily: theme?.fontFamily || 'Inter, sans-serif'
  };

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5"
      style={containerStyle}
    >
      {safeProducts.map((product) => (
        <ProductCard
          key={product.productId || product._id}
          product={product}
          onAddToCart={onAddToCart}
          theme={theme}
          bussinessUuid={bussinessUuid}
          customerProfile={customerProfile}
          displayBusinessName={displayBusinessName}
          previewMode={previewMode}
        />
      ))}
    </div>
  );
}
export default ProductCard;